/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { IUsageLimit } from '../@types/partial';
import type { XHubChatEvent } from '../models/event/event';

type IErrorJson = {
  [key: string]: any; // extensible
  errcode?: string;
  error?: string;
} & Partial<IUsageLimit>;

/**
 * Construct a generic HTTP error. This is a JavaScript Error with additional information
 * specific to HTTP responses.
 * @param msg - The error message to include.
 * @param httpStatus - The HTTP response status code.
 * @param httpHeaders - The HTTP response headers.
 */
export class HTTPError extends Error {
  public constructor(
    msg: string,
    public readonly httpStatus?: number,
    public readonly httpHeaders?: Headers,
  ) {
    super(msg);
  }

  /**
   * Check if this error was due to rate-limiting on the server side (and should therefore be retried after a delay).
   *
   * If this returns `true`, {@link getRetryAfterMs} can be called to retrieve the server-side
   * recommendation for the retry period.
   *
   * @returns Whether this error is due to rate-limiting.
   */
  public isRateLimitError(): boolean {
    return this.httpStatus === 429;
  }

  /**
   * @returns The recommended delay in milliseconds to wait before retrying
   * the request that triggered this error, or null if no delay is recommended.
   * @throws Error if the recommended delay is an invalid value.
   * @see {@link safeGetRetryAfterMs} for a version of this check that doesn't throw.
   */
  public getRetryAfterMs(): number | null {
    const retryAfter = this.httpHeaders?.get('Retry-After');
    if (retryAfter != null) {
      if (/^\d+$/.test(retryAfter)) {
        const ms = Number.parseInt(retryAfter) * 1000;
        if (!Number.isFinite(ms)) {
          throw new TypeError('Retry-After header integer value is too large');
        }
        return ms;
      }
      const date = new Date(retryAfter);
      if (date.toUTCString() !== retryAfter) {
        throw new Error('Retry-After header value is not a valid HTTP-date or non-negative decimal integer');
      }
      return date.getTime() - Date.now();
    }
    return null;
  }
}

export class XHubChatError extends HTTPError {
  // The XHubChat 'errcode' value, e.g. "M_FORBIDDEN".
  public readonly errcode?: string;
  // The raw XHubChat error JSON used to construct this object.
  public data: IErrorJson;

  /**
   * Construct an XHubChat error. This is a JavaScript Error with additional
   * information specific to the standard XHubChat error response.
   * @param errorJson - The XHubChat error JSON returned from the homeserver.
   * @param httpStatus - The numeric HTTP status code given
   * @param url - The request URL that generated this error, if known.
   * @param event - The event that triggered this error, if applicable.
   * @param httpHeaders - The HTTP response headers given
   * @throws Error if the errorJson is malformed.
   */
  public constructor(
    errorJson: IErrorJson = {},
    httpStatus?: number,
    public url?: string,
    public event?: XHubChatEvent,
    httpHeaders?: Headers,
  ) {
    let message = errorJson.error || 'Unknown message';
    if (httpStatus) {
      message = `[${httpStatus}] ${message}`;
    }
    if (url) {
      message = `${message} (${url})`;
    }
    super(`XHunChat: ${message}`, httpStatus, httpHeaders);
    this.errcode = errorJson.errcode;
    this.name = errorJson.errcode || 'Unknown error code';
    this.data = errorJson;
  }

  public isRateLimitError(): boolean {
    return (
      this.errcode === 'M_LIMIT_EXCEEDED'
      || ((this.errcode === 'M_UNKNOWN' || this.errcode === undefined) && super.isRateLimitError())
    );
  }

  public getRetryAfterMs(): number | null {
    const headerValue = super.getRetryAfterMs();
    if (headerValue !== null) {
      return headerValue;
    }
    // Note: retry_after_ms is deprecated as of spec version v1.10
    if (this.errcode === 'M_LIMIT_EXCEEDED' && 'retry_after_ms' in this.data) {
      if (!Number.isInteger(this.data.retry_after_ms)) {
        throw new TypeError('retry_after_ms is not an integer');
      }
      return this.data.retry_after_ms;
    }
    return null;
  }
}

/**
 * Construct a ConnectionError. This is a JavaScript Error indicating
 * that a request failed because of some error with the connection, either
 * CORS was not correctly configured on the server, the server didn't response,
 * the request timed out, or the internet connection on the client side went down.
 */
export class ConnectionError extends Error {
  public constructor(message: string, cause?: Error) {
    super(message + (cause ? `: ${cause.message}` : ''));
  }

  public get name(): string {
    return 'ConnectionError';
  }
}

/**
 * @returns The recommended delay in milliseconds to wait before retrying the request.
 * @param error - The error to check for a retry delay.
 * @param defaultMs - The delay to use if the error was not due to rate-limiting or if no valid delay is recommended.
 */
export function safeGetRetryAfterMs(error: unknown, defaultMs: number): number {
  if (!(error instanceof XHubChatError) || !error.isRateLimitError()) {
    return defaultMs;
  }
  try {
    return error.getRetryAfterMs() ?? defaultMs;
  } catch {
    return defaultMs;
  }
}

/**
 * Construct a TokenRefreshError. This indicates that a request failed due to the token being expired,
 * and attempting to refresh said token failed in a way indicative of token invalidation.
 */
export class TokenRefreshLogoutError extends Error {
  public constructor(cause?: Error) {
    super(cause?.message ?? '');
  }

  public get name(): string {
    return 'TokenRefreshLogoutError';
  }
}

/**
 * Construct a TokenRefreshError. This indicates that a request failed due to the token being expired,
 * and attempting to refresh said token also failed but in a way which was not indicative of token invalidation.
 * Assumed to be a temporary failure.
 */
export class TokenRefreshError extends Error {
  public constructor(cause?: Error) {
    super(cause?.message ?? '');
  }

  public get name(): string {
    return 'TokenRefreshError';
  }
}
